(function () {
  'use strict';

  if (window.ShimmerConfettiNeonV2Host) {
    window.ShimmerConfettiNeonV2Host.mount(document);
    return;
  }

  var ROOT_SELECTOR = '[data-sc-neon-v2]';
  var MESSAGE_SOURCE = 'shimmer-confetti-neon';
  var DESIGNER_RELEASE = '20261019-rollback2';
  var instances = new WeakMap();

  function safeUrl(value, base) {
    try {
      var url = new URL(value, base || window.location.href);
      return url.protocol === 'https:' || (url.protocol === 'http:' && url.hostname === 'localhost') ? url : null;
    } catch (error) {
      return null;
    }
  }

  function NeonDesignerHost(root) {
    this.root = root;
    this.frame = root.querySelector('[data-designer-frame]');
    this.loader = root.querySelector('[data-loader]');
    this.statusText = root.querySelector('[data-status-text]');
    this.fallback = root.querySelector('[data-fallback]');
    this.checkoutPanel = root.querySelector('[data-checkout-panel]');
    this.checkoutLink = root.querySelector('[data-checkout-link]');
    this.loadButton = root.querySelector('[data-load-designer]');
    this.retryButton = root.querySelector('[data-retry-designer]');
    this.directLink = root.querySelector('[data-direct-link]');
    this.maximumHeight = Math.max(900, Number(root.dataset.maxHeight) || 1800);
    this.designerUrl = safeUrl(root.dataset.designerUrl);
    this.designerOrigin = this.designerUrl ? this.designerUrl.origin : '';
    this.loaded = false;
    this.destroyed = false;
    this.loadTimer = null;

    this.onMessage = this.onMessage.bind(this);
    this.onFrameLoad = this.onFrameLoad.bind(this);
    this.onFrameError = this.onFrameError.bind(this);
    this.load = this.load.bind(this);
    this.retry = this.retry.bind(this);

    this.allowedCheckoutHosts = new Set([
      window.location.hostname,
      root.dataset.shopDomain,
      root.dataset.shopPermanentDomain,
      'checkout.shopify.com'
    ].filter(Boolean));

    this.init();
  }

  NeonDesignerHost.prototype.init = function () {
    window.addEventListener('message', this.onMessage);
    this.frame.addEventListener('load', this.onFrameLoad);
    this.frame.addEventListener('error', this.onFrameError);
    this.loadButton.addEventListener('click', this.load);
    this.retryButton.addEventListener('click', this.retry);

    if (!this.designerUrl) {
      this.fail('The custom neon designer URL is not configured correctly.');
      return;
    }

    this.directLink.href = this.buildDesignerUrl().href;

    if (window.location.hash === '#neon-designer') {
      this.load();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) {
          this.load();
          this.observer.disconnect();
        }
      }.bind(this), { rootMargin: '500px 0px' });
      this.observer.observe(this.root);
    } else {
      this.load();
    }
  };

  NeonDesignerHost.prototype.buildDesignerUrl = function () {
    var url = new URL(this.designerUrl.href);
    url.searchParams.set('embed', 'iframe');
    url.searchParams.set('parent_origin', window.location.origin);
    url.searchParams.set('locale', this.root.dataset.locale || document.documentElement.lang || 'en');
    url.searchParams.set('country', this.root.dataset.country || 'CA');
    url.searchParams.set('currency', this.root.dataset.currency || 'CAD');
    url.searchParams.set('shop', this.root.dataset.shopDomain || window.location.hostname);
    url.searchParams.set('release', DESIGNER_RELEASE);
    return url;
  };

  NeonDesignerHost.prototype.load = function () {
    if (this.loaded || this.destroyed || !this.designerUrl) return;
    this.loaded = true;
    this.loader.hidden = false;
    this.fallback.hidden = true;
    this.frame.hidden = false;
    this.statusText.textContent = 'Loading your neon design studio…';
    this.loadButton.hidden = true;
    this.frame.src = this.buildDesignerUrl().href;
    this.loadTimer = window.setTimeout(function () {
      this.fail('The designer is taking longer than expected.');
    }.bind(this), 25000);
  };

  NeonDesignerHost.prototype.retry = function () {
    window.clearTimeout(this.loadTimer);
    this.loaded = false;
    this.frame.hidden = true;
    this.frame.removeAttribute('src');
    this.loadButton.hidden = false;
    this.load();
  };

  NeonDesignerHost.prototype.onFrameLoad = function () {
    this.frame.hidden = false;
    this.statusText.textContent = 'Starting your neon design studio…';
    this.frame.contentWindow.postMessage({
      source: 'shimmer-confetti-shopify',
      type: 'sc-neon:context',
      detail: {
        market: {
          locale: this.root.dataset.locale,
          country: this.root.dataset.country,
          currency: this.root.dataset.currency
        }
      }
    }, this.designerOrigin);
  };

  NeonDesignerHost.prototype.markReady = function () {
    window.clearTimeout(this.loadTimer);
    this.loader.hidden = true;
    this.fallback.hidden = true;
    this.frame.hidden = false;
  };

  NeonDesignerHost.prototype.onFrameError = function () {
    this.fail('The designer could not be loaded.');
  };

  NeonDesignerHost.prototype.fail = function (message) {
    window.clearTimeout(this.loadTimer);
    this.loader.hidden = true;
    this.fallback.hidden = false;
    this.fallback.querySelector('strong').textContent = message;
  };

  NeonDesignerHost.prototype.onMessage = function (event) {
    if (event.origin !== this.designerOrigin || event.source !== this.frame.contentWindow) return;
    var message = event.data;
    if (!message || message.source !== MESSAGE_SOURCE || typeof message.type !== 'string') return;

    if (message.type === 'sc-neon:ready') {
      this.markReady();
      return;
    }

    if (message.type === 'sc-neon:height') {
      var requestedHeight = Number(message.detail && message.detail.height);
      if (!Number.isFinite(requestedHeight)) return;
      var responsiveMaximum = window.innerWidth <= 800 ? Math.max(this.maximumHeight, 6000) : this.maximumHeight;
      var height = Math.min(responsiveMaximum, Math.max(680, Math.ceil(requestedHeight)));
      this.frame.style.height = height + 'px';
      return;
    }

    if (message.type === 'sc-neon:navigate') {
      this.prepareCheckout(message.detail && message.detail.url);
    }
  };

  NeonDesignerHost.prototype.prepareCheckout = function (value) {
    var destination = safeUrl(value);
    if (!destination || !this.isAllowedCheckout(destination)) return;

    this.checkoutLink.href = destination.href;
    this.checkoutPanel.hidden = false;
    this.checkoutPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    this.checkoutLink.focus({ preventScroll: true });
  };

  NeonDesignerHost.prototype.isAllowedCheckout = function (url) {
    return this.allowedCheckoutHosts.has(url.hostname);
  };

  NeonDesignerHost.prototype.destroy = function () {
    this.destroyed = true;
    window.clearTimeout(this.loadTimer);
    window.removeEventListener('message', this.onMessage);
    this.frame.removeEventListener('load', this.onFrameLoad);
    this.frame.removeEventListener('error', this.onFrameError);
    this.loadButton.removeEventListener('click', this.load);
    this.retryButton.removeEventListener('click', this.retry);
    if (this.observer) this.observer.disconnect();
  };

  function mount(container) {
    var scope = container || document;
    scope.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (!instances.has(root)) instances.set(root, new NeonDesignerHost(root));
    });
  }

  function unmount(container) {
    if (!container) return;
    container.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      var instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(document); }, { once: true });
  } else {
    mount(document);
  }

  document.addEventListener('shopify:section:load', function (event) { mount(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { unmount(event.target); });

  window.ShimmerConfettiNeonV2Host = {
    mount: mount,
    unmount: unmount
  };
}());
